const CONTACT_SUBMISSION_FORM_SELECTOR = '[data-contact-submission-form]';
const DEFAULT_LANGUAGE = 'es';
const DEFAULT_COUNTRY = 'ES';
const DEFAULT_TYPE = 'contact';
const DEFAULT_PHONE = 'No informado';
const FILE_INPUT_NAME = 'contact[file]';
const SHOPIFY_INTERNAL_FIELDS = new Set(['form_type', 'utf8', 'contact[id]']);
const CONTACT_FIELDS_IGNORED_IN_BODY = new Set(['name', 'email', 'phone', 'body', 'file', 'accept']);
const PENDING_SUBMISSIONS_KEY = 'contact-submission:pending';
const PROCESSED_SUBMISSIONS_KEY = 'contact-submission:processed';
const FILE_DB_NAME = 'contact-submission-db';
const FILE_STORE_NAME = 'files';

const inFlightSubmissions = new Set();
const initializedForms = new WeakSet();
let initializedLifecycle = false;
let initializedProcessSuccessful = false;

export function initializeContactSubmissionForwarding(root = document) {
  const candidates = [...root.querySelectorAll(CONTACT_SUBMISSION_FORM_SELECTOR)];
  if (root instanceof HTMLFormElement && root.matches(CONTACT_SUBMISSION_FORM_SELECTOR)) {
    candidates.unshift(root);
  }
  const forms = candidates.filter((form) => form instanceof HTMLFormElement && !initializedForms.has(form));

  if (forms.length === 0) {
    return;
  }

  for (const form of forms) {
    initializedForms.add(form);
    form.addEventListener('submit', handleFormSubmit, true);
  }

  initializeSubmissionFeedback();
  if (initializedLifecycle) return;
  initializedLifecycle = true;

  const runProcessOnce = () => {
    if (initializedProcessSuccessful) {
      return;
    }
    initializedProcessSuccessful = true;
    void processSuccessfulSubmission();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runProcessOnce, { once: true });
  } else {
    runProcessOnce();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      void processSuccessfulSubmission();
    }
  });
}

function initializeSubmissionFeedback() {
  let targetId = window.location.hash.replace('#', '');

  if (!targetId) {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') === 'true') {
      const feedbackForm = document.querySelector('[data-form-submission-feedback]');
      if (feedbackForm?.id) {
        targetId = feedbackForm.id;
      }
    }
  }

  if (!targetId) {
    return;
  }

  const target = document.getElementById(targetId);

  if (!(target instanceof HTMLElement) || !target.matches('[data-form-submission-feedback]')) {
    return;
  }

  const parentDetails = target.closest('details');
  if (parentDetails instanceof HTMLDetailsElement) {
    parentDetails.open = true;
  }

  const successMessage = target.querySelector('[data-form-submission-success]');
  const formContent = target.querySelector('[data-form-submission-content]');

  if (!(successMessage instanceof HTMLElement) || !(formContent instanceof HTMLElement)) {
    return;
  }

  formContent.hidden = true;
  successMessage.hidden = false;
  successMessage.focus();
}

function handleFormSubmit(event) {
  const form = event.target instanceof HTMLFormElement
    ? event.target.closest(CONTACT_SUBMISSION_FORM_SELECTOR)
    : null;

  if (!(form instanceof HTMLFormElement)) {
    return;
  }

  const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
  if (submitButton instanceof HTMLButtonElement || submitButton instanceof HTMLInputElement) {
    setTimeout(() => {
      submitButton.disabled = true;
    }, 0);
  }

  void persistPendingSubmission(form);
}

async function persistPendingSubmission(form) {
  const pendingSubmissions = getPendingSubmissions();
  const fileInput = getFileInput(form);
  const file = fileInput?.files?.[0] || null;
  const submissionId = file ? `${form.id}:${Date.now()}` : null;

  if (submissionId && file) {
    await saveFile(submissionId, file);
  }

  pendingSubmissions[form.id] = {
    formId: form.id,
    action: form.dataset.contactSubmissionAction || '',
    type: form.dataset.contactSubmissionType || DEFAULT_TYPE,
    createdAt: Date.now(),
    payload: buildSerializablePayload(form),
    fileSubmissionId: submissionId,
  };

  sessionStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(pendingSubmissions));
}

async function processSuccessfulSubmission() {
  const formId = getSuccessfulFormIdFromUrl();

  if (!formId || inFlightSubmissions.has(formId)) {
    return;
  }

  const processedSubmissions = getProcessedSubmissions();
  if (processedSubmissions[formId]) {
    return;
  }

  const pendingSubmissions = getPendingSubmissions();
  const pendingSubmission = pendingSubmissions[formId];

  if (!pendingSubmission?.action) {
    return;
  }

  inFlightSubmissions.add(formId);
  delete pendingSubmissions[formId];
  sessionStorage.setItem(PENDING_SUBMISSIONS_KEY, JSON.stringify(pendingSubmissions));

  processedSubmissions[formId] = Date.now();
  sessionStorage.setItem(PROCESSED_SUBMISSIONS_KEY, JSON.stringify(processedSubmissions));

  try {
    const formData = buildAppPayloadFromStoredSubmission(pendingSubmission);
    const storedFile = pendingSubmission.fileSubmissionId
      ? await getFile(pendingSubmission.fileSubmissionId)
      : null;

    if (storedFile instanceof File) {
      formData.append('file', storedFile, storedFile.name);
    }

    await forwardSubmission(pendingSubmission.action, formData);

    if (pendingSubmission.fileSubmissionId) {
      await deleteFile(pendingSubmission.fileSubmissionId);
    }
  } catch (error) {
    console.error('Error forwarding successful Shopify contact submission to app proxy:', error);
  } finally {
    inFlightSubmissions.delete(formId);
  }
}

function getSuccessfulFormIdFromUrl() {
  const url = new URL(window.location.href);
  const postedSuccessfully = url.searchParams.get('contact_posted') === 'true';

  if (!postedSuccessfully) {
    return '';
  }

  const targetFormId = window.location.hash.replace('#', '');
  if (targetFormId.length > 0) {
    return targetFormId;
  }

  const pendingSubmissions = getPendingSubmissions();
  const keys = Object.keys(pendingSubmissions);
  if (keys.length === 1) {
    return keys[0];
  }

  const feedbackForm = document.querySelector('[data-form-submission-feedback]');
  if (feedbackForm?.id && pendingSubmissions[feedbackForm.id]) {
    return feedbackForm.id;
  }

  return '';
}

function buildSerializablePayload(form) {
  const surname = getFieldValue(form, 'contact[last_name]') || getFieldValue(form, 'contact[surname]');
  const body = buildBody(form) || (form.dataset.contactSubmissionType ? `Formulario de ${form.dataset.contactSubmissionType}` : 'Formulario de contacto');

  return {
    country: getCountry(),
    language: getLanguage(),
    name: getFieldValue(form, 'contact[name]'),
    surname,
    phone: getPhoneValue(form),
    email: getFieldValue(form, 'contact[email]'),
    body,
    type: form.dataset.contactSubmissionType || DEFAULT_TYPE,
  };
}

function buildAppPayloadFromStoredSubmission(submission) {
  const formData = new FormData();

  formData.append('country', submission.payload.country || DEFAULT_COUNTRY);
  formData.append('language', submission.payload.language || DEFAULT_LANGUAGE);
  formData.append('name', submission.payload.name || '');
  if (submission.payload.surname) {
    formData.append('surname', submission.payload.surname);
  }
  formData.append('phone', submission.payload.phone || DEFAULT_PHONE);
  formData.append('email', submission.payload.email || '');
  formData.append('body', submission.payload.body || '');
  formData.append('type', submission.payload.type || submission.type || DEFAULT_TYPE);

  return formData;
}

async function forwardSubmission(action, formData) {
  const response = await fetch(action, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Contact submission forwarding failed with status ${response.status}.`);
  }
}

function buildBody(form) {
  const lines = [];
  const customFields = getCustomFields(form);
  const comment = getFieldValue(form, 'contact[body]');

  for (const field of customFields) {
    lines.push(`${capitalize(field.label)}: ${field.value}`);
  }

  if (comment) {
    lines.push(`Comentarios: ${comment}`);
  }

  return lines.join('\n').trim();
}

function getCustomFields(form) {
  return Array.from(form.elements)
    .filter((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
        return false;
      }

      if (field.disabled || !field.name.startsWith('contact[') || SHOPIFY_INTERNAL_FIELDS.has(field.name)) {
        return false;
      }

      if ((field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) && !field.checked) {
        return false;
      }

      const key = getContactFieldKey(field.name);

      return key.length > 0 && !CONTACT_FIELDS_IGNORED_IN_BODY.has(key);
    })
    .map((field) => ({
      label: getContactFieldKey(field.name),
      value: field.value.trim(),
    }))
    .filter((field) => field.value.length > 0);
}

function getContactFieldKey(name) {
  return name.replace(/^contact\[/, '').replace(/\]$/, '');
}

function getFieldValue(form, name) {
  const field = form.querySelector(`[name="${CSS.escape(name)}"]`);

  if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
    return '';
  }

  if ((field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) && !field.checked) {
    return '';
  }

  return field.value.trim();
}

function getFileInput(form) {
  const input = form.querySelector(`[name="${CSS.escape(FILE_INPUT_NAME)}"]`);

  return input instanceof HTMLInputElement ? input : null;
}

function getPhoneValue(form) {
  const input = form.querySelector('[name="contact[phone]"]');

  if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement)) {
    return DEFAULT_PHONE;
  }

  return input.value.trim();
}

function getLanguage() {
  return window.Shopify?.locale || document.documentElement.lang || DEFAULT_LANGUAGE;
}

function getCountry() {
  return window.Shopify?.country || document.documentElement.getAttribute('data-country') || DEFAULT_COUNTRY;
}

function capitalize(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getPendingSubmissions() {
  try {
    const rawValue = sessionStorage.getItem(PENDING_SUBMISSIONS_KEY);

    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue);

    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function getProcessedSubmissions() {
  try {
    const rawValue = sessionStorage.getItem(PROCESSED_SUBMISSIONS_KEY);

    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue);

    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

async function saveFile(id, file) {
  const db = await openFileDatabase();

  await runTransaction(db, 'readwrite', (store) => store.put(file, id));
}

async function getFile(id) {
  const db = await openFileDatabase();

  return runTransaction(db, 'readonly', (store) => store.get(id));
}

async function deleteFile(id) {
  const db = await openFileDatabase();

  await runTransaction(db, 'readwrite', (store) => store.delete(id));
}

function openFileDatabase() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(FILE_DB_NAME, 1);

    request.addEventListener('upgradeneeded', () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(FILE_STORE_NAME)) {
        db.createObjectStore(FILE_STORE_NAME);
      }
    });

    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () => reject(request.error || new Error('Unable to open IndexedDB.')));
  });
}

function runTransaction(db, mode, operation) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(FILE_STORE_NAME, mode);
    const store = transaction.objectStore(FILE_STORE_NAME);
    const request = operation(store);

    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () => reject(request.error || new Error('IndexedDB request failed.')));
  });
}
